'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

interface ScanRecord {
  id: string;
  scannedAt: string;
  location?: string;
  isPublic?: boolean;
  userAgent?: string;
}

interface ScanHistoryModalProps {
  open: boolean;
  onClose: () => void;
  code: string;
}

export function ScanHistoryModal({ open, onClose, code }: ScanHistoryModalProps) {
  const t = useTranslations('public');
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    fetch(`/api/codes/${encodeURIComponent(code)}/details`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setScans(data.scans || []);
      })
      .catch(() => {
        // Silent fail
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, code]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col"
      style={{ background: 'var(--pub-surface)' }}
    >
      {/* Header */}
      <div
        className="flex justify-between items-center flex-shrink-0"
        style={{ padding: '52px 20px 12px' }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--font-pub-display)',
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--pub-text-primary)',
            }}
          >
            {t('scanHistory.title')}
          </h2>
          <p
            style={{
              fontFamily: 'var(--font-pub-mono)',
              fontSize: 10,
              color: 'var(--pub-text-muted)',
              marginTop: 3,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {scans.length > 0
              ? `${scans.length} ${t('scanHistory.scansTotal')}`
              : t('scanHistory.loading')}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center justify-center cursor-pointer transition-colors duration-200"
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            border: '1px solid var(--pub-border)',
            background: 'var(--pub-surface-2)',
            color: 'var(--pub-text-secondary)',
          }}
          aria-label={t('scanHistory.close')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Scan list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: '0 16px' }}>
        {loading && (
          <div className="flex items-center justify-center" style={{ padding: '40px 0' }}>
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: '2px solid var(--pub-border)',
                borderTopColor: 'var(--pub-green)',
                animation: 'pub-spin 1s linear infinite',
              }}
            />
          </div>
        )}

        {!loading && scans.length === 0 && (
          <div
            className="text-center"
            style={{ padding: '40px 0', color: 'var(--pub-text-muted)', fontSize: 13 }}
          >
            {t('scanHistory.empty')}
          </div>
        )}

        {!loading && scans.map((scan, i) => {
          const date = new Date(scan.scannedAt);
          const formatted = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });

          // Detect device type from user agent
          const isMobile = scan.userAgent?.match(/Mobile|Android|iPhone/i);
          const deviceLabel = isMobile ? 'Mobile' : 'Desktop';

          return (
            <div
              key={scan.id}
              className="flex items-center gap-3"
              style={{
                padding: '12px 0',
                borderBottom: i < scans.length - 1 ? '1px solid var(--pub-border-subtle)' : 'none',
              }}
            >
              {/* Scan number */}
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'var(--pub-surface-3)',
                  border: '1px solid var(--pub-border)',
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--pub-text-muted)',
                }}
              >
                {i + 1}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div style={{ fontSize: 13, color: 'var(--pub-text-primary)', fontWeight: 500 }}>
                  {formatted}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-pub-mono)',
                    fontSize: 10,
                    color: 'var(--pub-text-muted)',
                    marginTop: 2,
                  }}
                >
                  {scan.location || deviceLabel}
                </div>
              </div>

              {/* Public badge */}
              {scan.isPublic && (
                <span
                  style={{
                    fontFamily: 'var(--font-pub-mono)',
                    fontSize: 9,
                    color: 'var(--pub-green)',
                    background: 'var(--pub-green-dim)',
                    padding: '2px 8px',
                    borderRadius: 100,
                    letterSpacing: '0.06em',
                  }}
                >
                  PUBLIC
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0" style={{ padding: '14px 16px 32px' }}>
        <button className="pub-btn pub-btn-secondary" onClick={onClose}>
          {t('scanHistory.close')}
        </button>
      </div>
    </div>
  );
}
